<script setup lang="ts">
import { ToolButton } from '@/components';
import { SvgExhibition } from '@repo/icons';
import { useRouter } from 'vue-router';
import { showNotify } from 'vant';
import { useFabricStore } from '@/store/fabric.ts';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { loadI18nMessages } from '@repo/locales';
import type { SupportedLanguagesType } from '@repo/locales';

const router = useRouter();
const fabricStore = useFabricStore();

const onClickExhibition = async () => {
  if (!fabricStore?.fabricInfo?.enterpriseId) {
    showNotify({ type: 'danger', message: '获取企业id失败' });
    return;
  }
  await router.push({
    name: 'exhibition-list',
    params: {
      enterpriseId: fabricStore.fabricInfo.enterpriseId
    }
  });
};

// 语言切换
const currentLang = ref<SupportedLanguagesType>(
  (document.querySelector('html')?.getAttribute('lang') as SupportedLanguagesType) || 'zh'
);
const showLangDropdown = ref(false);

const langOptions: { label: string; value: SupportedLanguagesType }[] = [
  { label: '简体中文', value: 'zh' },
  { label: 'English', value: 'en' },
];

const currentLangLabel = computed(
  () => langOptions.find(o => o.value === currentLang.value)?.label ?? '简体中文'
);

const toggleDropdown = () => {
  showLangDropdown.value = !showLangDropdown.value;
};

const selectLang = async (lang: SupportedLanguagesType) => {
  if (lang === currentLang.value) {
    showLangDropdown.value = false;
    return;
  }
  currentLang.value = lang;
  showLangDropdown.value = false;
  await loadI18nMessages(lang);
};

// 点击外部关闭下拉框
const langWrapperRef = ref<HTMLElement | null>(null);
const onClickOutside = (e: MouseEvent) => {
  if (langWrapperRef.value && !langWrapperRef.value.contains(e.target as Node)) {
    showLangDropdown.value = false;
  }
};
onMounted(() => document.addEventListener('click', onClickOutside));
onUnmounted(() => document.removeEventListener('click', onClickOutside));
</script>

<template>
  <div :class="$style.row">
    <!-- 语言切换 -->
    <div :class="$style.langWrapper" ref="langWrapperRef">
      <ToolButton @click="toggleDropdown" :class="$style.langBtn">
        <span :class="$style.langLabel">{{ currentLangLabel }}</span>
        <span :class="[$style.arrow, showLangDropdown && $style.arrowUp]">▾</span>
      </ToolButton>
      <div v-if="showLangDropdown" :class="$style.dropdown">
        <div
          v-for="opt in langOptions"
          :key="opt.value"
          :class="[$style.dropdownItem, opt.value === currentLang && $style.active]"
          @click="selectLang(opt.value)"
        >
          {{ opt.label }}
        </div>
      </div>
    </div>

    <!-- Exhibition 按钮 -->
    <ToolButton @click="onClickExhibition" id="exhibition-button">
      <SvgExhibition style="width: 20px;height:20px;" />
    </ToolButton>
  </div>
</template>

<style module>
.row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
}

.langWrapper {
  position: relative;
}

.langBtn {
  white-space: nowrap;
}

.langLabel {
  font-size: 13px;
  margin-right: 2px;
}

.arrow {
  font-size: 12px;
  display: inline-block;
  transition: transform 0.2s;
}

.arrowUp {
  transform: rotate(180deg);
}

.dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  min-width: 100px;
  z-index: 100;
  overflow: hidden;
}

.dropdownItem {
  padding: 8px 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
}

.dropdownItem:hover {
  background: #f5f5f5;
}

.active {
  color: #1989fa;
  font-weight: 500;
}
</style>
